export interface ProductData {
  id: string;
  name: string;
  tagline: string;
  shortDescription: string;
  fullDescription: string;
  category: string;
  categoryId: string;
  icon: string;
  accentImage: string;
  features: Array<{
    title: string;
    description: string;
    icon: string;
  }>;
  useCases: Array<{
    title: string;
    description: string;
  }>;
  screenshots: Array<{
    label: string;
    image: string;
  }>;
  integrations: string[];
  benefits: string[];
  stats?: Array<{
    value: string;
    label: string;
  }>;
}

export const productsData: Record<string, ProductData> = {
  worktrack: {
    id: "worktrack",
    name: "WorkTrack",
    tagline: "See everything your team is doing, in real time, wherever they work.",
    shortDescription:
      "See everything your team is doing, in real time, wherever they work. WorkTrack replaces scattered check-ins, WhatsApp groups, and manual timesheets with a single connected workforce visibility system.",
    fullDescription:
      "WorkTrack gives supervisors and operations managers full visibility into what their teams are doing, where they are, and how tasks are progressing. Built for businesses with mobile, remote, or shift-based workforces, WorkTrack replaces manual check-ins with automated tracking, progress dashboards, and real-time status updates.",
    category: "Workforce Management",
    categoryId: "operations",
    icon: "ri-map-pin-user-line",
    accentImage:
      "https://readdy.ai/api/search-image?query=3D%20isometric%20dark%20mode%20enterprise%20system%20visualization%20of%20a%20workforce%20control%20dashboard%20with%20glowing%20activity%20nodes%20connected%20by%20tracking%20lines%20across%20a%20layered%20system%20interface%2C%20map-like%20grid%20with%20status%20indicators%2C%20clean%20minimal%20dark%20graphite%20background%20with%20subtle%20lighting%2C%20technical%20precise%20render%20style%2C%20no%20text%20no%20logos%2C%20no%20people%2C%20subtle%20emerald%20green%20accent%20highlights%20on%20active%20tracking%20nodes%2C%20consistent%20lighting%20from%20upper%20left%2C%20sharp%20focus%2C%20high%20detail&width=900&height=500&seq=worktrack-hero&orientation=landscape",
    features: [
      {
        title: "Staff Login, Activity & Idle Tracking",
        description:
          "Know who is logged in, active, or idle at any moment. Automated activity tracking gives supervisors real-time visibility into workforce engagement without manual check-ins.",
        icon: "ri-user-follow-line",
      },
      {
        title: "Work Mode Selection",
        description:
          "Switch between Office, Remote, and Field modes. Each mode configures relevant tracking, task assignment, and location options to match where the work is happening.",
        icon: "ri-briefcase-line",
      },
      {
        title: "Task Assignment, Deadlines & Progress",
        description:
          "Assign tasks with priorities and deadlines. Track completion status, delays, and bottlenecks across teams from a single live dashboard.",
        icon: "ri-calendar-check-line",
      },
      {
        title: "Structured Daily Reporting",
        description:
          "Standardized daily reports capture what was completed, what is pending, and what needs follow-up. Build a consistent operational record across every team member.",
        icon: "ri-file-list-3-line",
      },
      {
        title: "Back-and-Forth Task Communication",
        description:
          "Staff and supervisors can discuss tasks in threaded conversations attached to each assignment. No more lost messages or context switching between apps.",
        icon: "ri-chat-3-line",
      },
      {
        title: "Escalation Management with Approval Chains",
        description:
          "Define escalation rules and approval workflows for tasks that need sign-off. Route issues up the chain automatically based on type, urgency, or threshold.",
        icon: "ri-arrow-up-circle-line",
      },
      {
        title: "Internal Work-Based Messaging",
        description:
          "Built-in messaging centered around work: tasks, teams, and operations. Separate from personal chat apps and fully tied to your organizational structure.",
        icon: "ri-message-3-line",
      },
      {
        title: "Customer Service Ticketing",
        description:
          "Convert customer requests into structured tickets with assignment, SLA tracking, status updates, and resolution history. Keep service delivery organized and measurable.",
        icon: "ri-ticket-line",
      },
      {
        title: "Sales Activity Tracking (Light CRM)",
        description:
          "Log leads, calls, visits, and follow-ups. Track deal stages and activity pipelines to give sales managers visibility without a full CRM overhead.",
        icon: "ri-customer-service-line",
      },
      {
        title: "Productivity Insights & Performance Scoring",
        description:
          "Measure output per worker, team, or site. Identify high performers, spot inefficiencies, and make data-backed staffing and coaching decisions.",
        icon: "ri-bar-chart-box-line",
      },
      {
        title: "GPS & Location Tracking",
        description:
          "Optional GPS monitoring during active shifts. Admin-controlled toggle lets you enable location tracking where relevant. Verified field presence and route optimization included.",
        icon: "ri-map-pin-2-line",
      },
      {
        title: "God's Eye View Management Dashboard",
        description:
          "One screen showing everything: who is active, what tasks are open, where people are, and what needs attention. The command center for operations managers.",
        icon: "ri-eye-line",
      },
    ],
    useCases: [
      {
        title: "Field Service Teams",
        description:
          "Dispatch technicians with real-time job tracking. Customers get ETAs, and managers see who's on-site and how jobs are progressing.",
      },
      {
        title: "Warehouse & Fulfillment",
        description:
          "Track picking, packing, and put-away tasks by zone and worker. Identify slow zones and rebalance workloads to hit throughput targets.",
      },
      {
        title: "Security & Patrol Operations",
        description:
          "Verify patrol routes with GPS checkpoints. Guards scan NFC tags at patrol points, and supervisors get real-time patrol maps.",
      },
      {
        title: "Construction & Projects",
        description:
          "Track daily crew activities, material usage, and milestone completion across multiple job sites from one central view.",
      },
    ],
    screenshots: [
      {
        label: "Task Dashboard",
        image:
          "https://readdy.ai/api/search-image?query=3D%20isometric%20dark%20mode%20enterprise%20dashboard%20interface%20showing%20task%20cards%20in%20a%20kanban%20layout%20with%20status%20indicators%2C%20worker%20avatars%2C%20priority%20badges%2C%20clean%20minimal%20dark%20graphite%20UI%2C%20technical%20precise%20render%2C%20no%20text%2C%20subtle%20emerald%20green%20accents%2C%20consistent%20lighting%2C%20sharp%20focus%2C%20high%20detail&width=800&height=450&seq=worktrack-ss1&orientation=landscape",
      },
      {
        label: "Live Map View",
        image:
          "https://readdy.ai/api/search-image?query=3D%20isometric%20dark%20mode%20enterprise%20map%20interface%20with%20glowing%20location%20pins%2C%20team%20markers%20on%20a%20grid%2C%20route%20lines%20connecting%20check-in%20points%2C%20clean%20minimal%20dark%20graphite%20UI%2C%20technical%20precise%20render%2C%20no%20text%2C%20subtle%20emerald%20green%20accents%2C%20consistent%20lighting%2C%20sharp%20focus%2C%20high%20detail&width=800&height=450&seq=worktrack-ss2&orientation=landscape",
      },
      {
        label: "Productivity Report",
        image:
          "https://readdy.ai/api/search-image?query=3D%20isometric%20dark%20mode%20enterprise%20analytics%20report%20with%20bar%20charts%2C%20trend%20lines%2C%20worker%20comparison%20grids%2C%20and%20output%20metrics%2C%20clean%20minimal%20dark%20graphite%20UI%2C%20technical%20precise%20render%2C%20no%20text%2C%20subtle%20emerald%20green%20accents%2C%20consistent%20lighting%2C%20sharp%20focus%2C%20high%20detail&width=800&height=450&seq=worktrack-ss3&orientation=landscape",
      },
    ],
    integrations: ["HCM", "PMS", "OMS", "RemoteOps Hub", "Finance"],
    benefits: [
      "Replace scattered check-ins with a single connected system",
      "Get real-time visibility into team activity and task progress",
      "Improve accountability with digital audit trails and performance scoring",
      "Handle task communication, escalations, and ticketing in one place",
    ],
    stats: [
      { value: "12", label: "Core capabilities" },
      { value: "Real-time", label: "Team visibility" },
      { value: "100%", label: "Digital audit trail" },
    ],
  },
  hris: {
    id: "hris",
    name: "HCM",
    tagline: "Manage your people from hire to exit, in one place.",
    shortDescription:
      "Manage your people from hire to exit, in one place. HRIS replaces scattered spreadsheets and disconnected HR tools with a single source of truth for every employee, from onboarding through offboarding.",
    fullDescription:
      "HRIS replaces spreadsheets and disconnected HR tools with a single source of truth for your workforce. From onboarding to offboarding, it manages employee data, tracks attendance and leave, supports payroll preparation, and keeps compliance documentation organized and accessible.",
    category: "Human Resources",
    categoryId: "people-performance",
    icon: "ri-user-settings-line",
    accentImage:
      "https://readdy.ai/api/search-image?query=3D%20isometric%20dark%20mode%20enterprise%20system%20visualization%20of%20a%20structured%20people%20management%20system%20with%20hierarchical%20organization%20nodes%2C%20employee%20record%20cards%20arranged%20in%20connected%20layers%2C%20database%20tables%20and%20profile%20grids%20floating%20in%20space%2C%20clean%20minimal%20dark%20graphite%20background%20with%20subtle%20lighting%2C%20technical%20precise%20render%20style%2C%20no%20text%20no%20logos%2C%20no%20people%2C%20subtle%20emerald%20green%20accent%20highlights%20on%20active%20nodes%2C%20consistent%20lighting%20from%20upper%20left%2C%20sharp%20focus%2C%20high%20detail&width=900&height=500&seq=hris-hero&orientation=landscape",
    features: [
      {
        title: "Full Employee Records & Lifecycle Management",
        description:
          "Centralized profiles with job history, documents, contacts, and custom fields. Track every stage of the employee journey with full audit logging.",
        icon: "ri-id-card-line",
      },
      {
        title: "Onboarding & Offboarding Workflows",
        description:
          "Structured workflows for new hires and exits. Assign tasks, track completion, and ensure nothing falls through the cracks.",
        icon: "ri-user-add-line",
      },
      {
        title: "Leave & Absence Management",
        description:
          "Digital clock-in with shift rules. Manage leave requests, approvals, and balances automatically with policy enforcement.",
        icon: "ri-calendar-event-line",
      },
      {
        title: "Document Management",
        description:
          "Store contracts, IDs, certificates, and compliance documents with expiry alerts. Always audit-ready with version control.",
        icon: "ri-folder-shield-line",
      },
      {
        title: "Employee Self-Service Portal",
        description:
          "Empower staff to view payslips, request leave, update personal details, and access documents, reducing HR admin burden.",
        icon: "ri-user-line",
      },
      {
        title: "Organisational Structure & Hierarchy Mapping",
        description:
          "Live org chart synced to reporting lines. Searchable directory with role, department, and contact info. Visualise your entire workforce structure.",
        icon: "ri-organization-chart",
      },
      {
        title: "Internal Announcements & Communications",
        description:
          "Broadcast company-wide updates, policy changes, and team news. Targeted distribution by department, role, or location.",
        icon: "ri-megaphone-line",
      },
      {
        title: "Queries & Disciplinary Tracking",
        description:
          "Log, track, and manage employee grievances, disciplinary cases, and HR queries with structured case management and escalation.",
        icon: "ri-shield-check-line",
      },
      {
        title: "Attendance Tracking",
        description:
          "Integrates natively with WorkTrack for real-time attendance, shift data, and timesheet sync. No duplicate data entry.",
        icon: "ri-time-line",
      },
      {
        title: "Approvals for Leave & HR Requests",
        description:
          "Configurable approval chains for leave, expenses, and HR requests. Route to the right manager with automated reminders and escalation.",
        icon: "ri-check-double-line",
      },
      {
        title: "Staff Events",
        description:
          "Track birthdays, work anniversaries, team milestones, and internal celebrations. Keep your workplace culture visible and connected.",
        icon: "ri-cake-line",
      },
    ],
    useCases: [
      {
        title: "Multi-Location Enterprises",
        description:
          "Manage employees across branches, warehouses, and regional offices with localized policies and centralized oversight.",
      },
      {
        title: "Shift-Based Workforces",
        description:
          "Handle complex rotating shifts, overtime rules, and leave accruals for manufacturing, security, and logistics teams.",
      },
      {
        title: "Growing Startups",
        description:
          "Scale from 10 to 1,000 employees without switching systems. Structured onboarding keeps culture consistent as you grow.",
      },
      {
        title: "Regulated Industries",
        description:
          "Maintain compliance documentation, training records, and certification expiry tracking for audit and certification bodies.",
      },
    ],
    screenshots: [
      {
        label: "Employee Directory",
        image:
          "https://readdy.ai/api/search-image?query=3D%20isometric%20dark%20mode%20enterprise%20employee%20directory%20interface%20with%20profile%20cards%2C%20search%20bar%2C%20department%20filters%2C%20and%20status%20indicators%2C%20clean%20minimal%20dark%20graphite%20UI%2C%20technical%20precise%20render%2C%20no%20text%2C%20subtle%20emerald%20green%20accents%2C%20consistent%20lighting%2C%20sharp%20focus%2C%20high%20detail&width=800&height=450&seq=hris-ss1&orientation=landscape",
      },
      {
        label: "Attendance Dashboard",
        image:
          "https://readdy.ai/api/search-image?query=3D%20isometric%20dark%20mode%20enterprise%20attendance%20dashboard%20with%20calendar%20grid%2C%20clock-in%20indicators%2C%20leave%20request%20cards%2C%20and%20shift%20overlays%2C%20clean%20minimal%20dark%20graphite%20UI%2C%20technical%20precise%20render%2C%20no%20text%2C%20subtle%20emerald%20green%20accents%2C%20consistent%20lighting%2C%20sharp%20focus%2C%20high%20detail&width=800&height=450&seq=hris-ss2&orientation=landscape",
      },
      {
        label: "Org Chart",
        image:
          "https://readdy.ai/api/search-image?query=3D%20isometric%20dark%20mode%20enterprise%20org%20chart%20with%20hierarchical%20tree%20structure%2C%20department%20nodes%2C%20reporting%20lines%2C%20and%20expandable%20profile%20cards%2C%20clean%20minimal%20dark%20graphite%20UI%2C%20technical%20precise%20render%2C%20no%20text%2C%20subtle%20emerald%20green%20accents%2C%20consistent%20lighting%2C%20sharp%20focus%2C%20high%20detail&width=800&height=450&seq=hris-ss3&orientation=landscape",
      },
    ],
    integrations: ["WorkTrack", "PMS", "Learn", "Finance", "Admin"],
    benefits: [
      "Eliminate duplicate employee data across systems",
      "Reduce HR admin time by 60% with self-service",
      "Ensure compliance with automated document tracking",
      "Improve employee experience from hire to exit",
    ],
    stats: [
      { value: "11", label: "Core capabilities" },
      { value: "60%", label: "Less HR admin" },
      { value: "100%", label: "Audit-ready records" },
    ],
  },
  pms: {
    id: "pms",
    name: "PMS",
    tagline: "Set targets, track performance, and build a culture of accountability.",
    shortDescription:
      "Set targets, track performance, and build a culture of accountability. PMS uses the Balanced Scorecard framework to structure KPIs, cascade goals across teams, and turn performance reviews into a continuous operating rhythm.",
    fullDescription:
      "PMS transforms performance management from an annual checkbox into a continuous operating rhythm. Managers and employees set aligned goals, track progress against KPIs, and conduct structured reviews, all within a system that links individual performance to team and business outcomes.",
    category: "Performance Management",
    categoryId: "people-performance",
    icon: "ri-bar-chart-grouped-line",
    accentImage:
      "https://readdy.ai/api/search-image?query=3D%20isometric%20dark%20mode%20enterprise%20system%20visualization%20of%20a%20performance%20metrics%20dashboard%20with%20KPI%20scorecards%2C%20circular%20progress%20gauges%2C%20bar%20charts%2C%20and%20scoring%20grids%20floating%20in%20a%20structured%20layout%2C%20clean%20minimal%20dark%20graphite%20background%20with%20subtle%20lighting%2C%20technical%20precise%20render%20style%2C%20no%20text%20no%20logos%2C%20no%20people%2C%20subtle%20emerald%20green%20accent%20highlights%20on%20high%20scores%2C%20consistent%20lighting%20from%20upper%20left%2C%20sharp%20focus%2C%20high%20detail&width=900&height=500&seq=pms-hero&orientation=landscape",
    features: [
      {
        title: "Goal Setting & Alignment",
        description:
          "Set cascading OKRs or KPIs from company objectives down to individual contributors. Everyone sees how their work connects to the bigger picture.",
        icon: "ri-focus-3-line",
      },
      {
        title: "Real-Time KPI Tracking",
        description:
          "Dashboards update as work happens. Track completion rates, quality scores, sales targets, or any custom metric you define.",
        icon: "ri-dashboard-line",
      },
      {
        title: "Structured Performance Reviews",
        description:
          "Run quarterly or annual reviews with standardized forms, 360-degree feedback, and manager sign-off workflows.",
        icon: "ri-clipboard-line",
      },
      {
        title: "Scorecards & Ratings",
        description:
          "Weighted scorecards auto-calculate from tracked metrics. Eliminate subjective bias with data-driven ratings.",
        icon: "ri-star-line",
      },
      {
        title: "Feedback & Check-Ins",
        description:
          "Continuous feedback loops between managers and reports. Scheduled check-ins with notes, action items, and follow-ups.",
        icon: "ri-chat-3-line",
      },
      {
        title: "Performance Analytics",
        description:
          "Compare performance across teams, roles, and time periods. Spot trends, identify coaching needs, and recognize top talent.",
        icon: "ri-line-chart-line",
      },
    ],
    useCases: [
      {
        title: "Sales Organizations",
        description:
          "Track quota attainment, pipeline contribution, and activity metrics. Tie commissions to verified performance data.",
      },
      {
        title: "Service Teams",
        description:
          "Measure response times, resolution rates, and CSAT scores. Identify coaching opportunities from the data.",
      },
      {
        title: "Manufacturing Units",
        description:
          "Link production output, quality pass rates, and safety compliance to team and individual performance reviews.",
      },
      {
        title: "Professional Services",
        description:
          "Track billable hours, project delivery, and client satisfaction as structured performance indicators.",
      },
    ],
    screenshots: [
      {
        label: "KPI Dashboard",
        image:
          "https://readdy.ai/api/search-image?query=3D%20isometric%20dark%20mode%20enterprise%20KPI%20dashboard%20with%20scorecards%2C%20gauge%20charts%2C%20trend%20graphs%2C%20and%20goal%20progress%20bars%2C%20clean%20minimal%20dark%20graphite%20UI%2C%20technical%20precise%20render%2C%20no%20text%2C%20subtle%20emerald%20green%20accents%2C%20consistent%20lighting%2C%20sharp%20focus%2C%20high%20detail&width=800&height=450&seq=pms-ss1&orientation=landscape",
      },
      {
        label: "Review Workflow",
        image:
          "https://readdy.ai/api/search-image?query=3D%20isometric%20dark%20mode%20enterprise%20review%20workflow%20interface%20with%20form%20fields%2C%20rating%20scales%2C%20feedback%20panels%2C%20and%20approval%20stages%2C%20clean%20minimal%20dark%20graphite%20UI%2C%20technical%20precise%20render%2C%20no%20text%2C%20subtle%20emerald%20green%20accents%2C%20consistent%20lighting%2C%20sharp%20focus%2C%20high%20detail&width=800&height=450&seq=pms-ss2&orientation=landscape",
      },
      {
        label: "Team Comparison",
        image:
          "https://readdy.ai/api/search-image?query=3D%20isometric%20dark%20mode%20enterprise%20team%20comparison%20analytics%20with%20ranking%20tables%2C%20heatmaps%2C%20performance%20distribution%20charts%2C%20clean%20minimal%20dark%20graphite%20UI%2C%20technical%20precise%20render%2C%20no%20text%2C%20subtle%20emerald%20green%20accents%2C%20consistent%20lighting%2C%20sharp%20focus%2C%20high%20detail&width=800&height=450&seq=pms-ss3&orientation=landscape",
      },
    ],
    integrations: ["HCM", "WorkTrack", "Learn", "RemoteOps Hub"],
    benefits: [
      "Replace annual reviews with continuous performance visibility",
      "Reduce manager admin time by 50%",
      "Increase goal attainment with real-time tracking",
      "Build a data-backed talent identification pipeline",
    ],
    stats: [
      { value: "50%", label: "Less admin time" },
      { value: "3x", label: "More frequent check-ins" },
      { value: "90%", label: "Goal alignment rate" },
    ],
  },
  oms: {
    id: "oms",
    name: "OMS",
    tagline: "Run your warehouse, manage your inventory, and serve your clients with precision.",
    shortDescription:
      "Run your warehouse, manage your inventory, and serve your clients with precision. Track stock movements, coordinate inbound and outbound operations, manage replenishment, and give merchants and clients real-time visibility through a dedicated portal.",
    fullDescription:
      "OMS is the operational backbone for logistics companies, 3PL operators, warehouses, FMCG distributors, and retail operations. It provides real-time inventory tracking with SKU-level visibility, structured inbound and outbound workflows, replenishment monitoring, picking and dispatch management, cycle counts, and a merchant and client portal, all with full audit traceability. Operational staff activity in OMS feeds directly into WorkTrack productivity reporting when both apps are active.",
    category: "Order Management (Standalone)",
    categoryId: "order-management",
    icon: "ri-shopping-cart-2-line",
    accentImage:
      "https://readdy.ai/api/search-image?query=3D%20isometric%20dark%20mode%20enterprise%20system%20visualization%20of%20an%20order%20and%20logistics%20pipeline%20with%20flowing%20package%20nodes%20moving%20through%20connected%20system%20layers%2C%20tracking%20path%20lines%2C%20warehouse%20grid%20segments%2C%20and%20delivery%20status%20indicators%2C%20clean%20minimal%20dark%20graphite%20background%20with%20subtle%20lighting%2C%20technical%20precise%20render%20style%2C%20no%20text%20no%20logos%2C%20no%20people%2C%20subtle%20emerald%20green%20accent%20highlights%20on%20active%20flow%20paths%2C%20consistent%20lighting%20from%20upper%20left%2C%20sharp%20focus%2C%20high%20detail&width=900&height=500&seq=oms-hero&orientation=landscape",
    features: [
      {
        title: "Inventory Tracking",
        description:
          "Real-time visibility into stock levels, locations, and movements. Track every SKU from receiving through dispatch with precise location and quantity data.",
        icon: "ri-archive-line",
      },
      {
        title: "Inbound and Outbound Operations",
        description:
          "Manage goods receiving, put-away, picking, packing, and shipping in structured workflows. Coordinate dock schedules and carrier appointments for smooth throughput.",
        icon: "ri-truck-line",
      },
      {
        title: "Replenishment Tracking",
        description:
          "Monitor stock depletion and trigger replenishment orders before stockouts. Track purchase orders, expected arrivals, and backorder status in one view.",
        icon: "ri-arrow-up-circle-line",
      },
      {
        title: "Picking and Dispatch Management",
        description:
          "Optimize picking routes by zone, batch, or wave. Assign dispatch tasks, verify loads, and track shipments from handover through proof of delivery.",
        icon: "ri-shopping-bag-line",
      },
      {
        title: "Cycle Counts",
        description:
          "Run scheduled or ad-hoc physical counts by location, SKU, or zone. Reconcile variances, investigate discrepancies, and maintain inventory accuracy without shutting down operations.",
        icon: "ri-scan-line",
      },
      {
        title: "Merchant and Client Portal",
        description:
          "Give your merchants and clients a self-service portal to view inventory levels, order status, inbound receipts, and dispatch confirmations, reducing inquiry volume and building trust.",
        icon: "ri-user-community-line",
      },
      {
        title: "SKU & Product Catalog",
        description:
          "Manage products with variants, barcodes, categories, and custom attributes. Bulk import and export with validation to keep your catalog structured.",
        icon: "ri-barcode-line",
      },
      {
        title: "Multi-Location Stock Tracking",
        description:
          "See stock levels by warehouse, zone, shelf, or bin. Transfer stock between locations with tracked movements and always know where every unit sits.",
        icon: "ri-building-line",
      },
      {
        title: "Stock Movement History",
        description:
          "Full traceability from receiving through put-away, picking, adjustment, and dispatch. Know who did what and when, with a complete digital audit trail.",
        icon: "ri-history-line",
      },
      {
        title: "Reorder & Threshold Alerts",
        description:
          "Set min/max levels per SKU per location. Get alerts before stockouts, and auto-generate purchase suggestions to keep replenishment proactive.",
        icon: "ri-notification-3-line",
      },
      {
        title: "Demand Signals & Forecasting",
        description:
          "View demand trends from WorkTrack activity. Plan procurement based on actual consumption patterns, not guesses.",
        icon: "ri-arrow-up-line",
      },
    ],
    useCases: [
      {
        title: "Logistics Companies",
        description:
          "Coordinate multi-client warehousing with segregated inventory, shared resource pools, and client-specific reporting and SLAs.",
      },
      {
        title: "3PL Operators",
        description:
          "Manage contracts, billing by volume or transaction, and real-time client visibility from a single operations platform.",
      },
      {
        title: "Warehouses",
        description:
          "Optimize receiving, put-away, picking, and shipping workflows. Track labor productivity and equipment utilization alongside inventory accuracy.",
      },
      {
        title: "FMCG Distributors",
        description:
          "Handle high-velocity stock with batch tracking, expiry management, and rapid replenishment cycles tied to demand signals.",
      },
    ],
    screenshots: [
      {
        label: "Inventory Overview",
        image:
          "https://readdy.ai/api/search-image?query=3D%20isometric%20dark%20mode%20enterprise%20inventory%20overview%20dashboard%20with%20stock%20cards%2C%20location%20tabs%2C%20movement%20indicators%2C%20and%20alert%20badges%2C%20clean%20minimal%20dark%20graphite%20UI%2C%20technical%20precise%20render%2C%20no%20text%2C%20subtle%20emerald%20green%20accents%2C%20consistent%20lighting%2C%20sharp%20focus%2C%20high%20detail&width=800&height=450&seq=oms-ss1&orientation=landscape",
      },
      {
        label: "Inbound / Outbound Pipeline",
        image:
          "https://readdy.ai/api/search-image?query=3D%20isometric%20dark%20mode%20enterprise%20inbound%20outbound%20pipeline%20with%20receiving%20nodes%2C%20picking%20lanes%2C%20dispatch%20lanes%2C%20and%20status%20flow%20arrows%2C%20clean%20minimal%20dark%20graphite%20UI%2C%20technical%20precise%20render%2C%20no%20text%2C%20subtle%20emerald%20green%20accents%2C%20consistent%20lighting%2C%20sharp%20focus%2C%20high%20detail&width=800&height=450&seq=oms-ss2&orientation=landscape",
      },
      {
        label: "Client Portal",
        image:
          "https://readdy.ai/api/search-image?query=3D%20isometric%20dark%20mode%20enterprise%20client%20portal%20interface%20with%20inventory%20summaries%2C%20order%20status%20cards%2C%20receipt%20logs%2C%20and%20dispatch%20confirmations%2C%20clean%20minimal%20dark%20graphite%20UI%2C%20technical%20precise%20render%2C%20no%20text%2C%20subtle%20emerald%20green%20accents%2C%20consistent%20lighting%2C%20sharp%20focus%2C%20high%20detail&width=800&height=450&seq=oms-ss3&orientation=landscape",
      },
    ],
    integrations: ["WorkTrack", "Finance", "Admin", "RemoteOps Hub"],
    benefits: [
      "Run warehouse and inventory operations with real-time precision",
      "Feed OMS operational activity directly into WorkTrack productivity reports",
      "Reduce client inquiry volume with self-service portal visibility",
      "Maintain inventory accuracy with integrated cycle counts, reorder alerts, and full movement traceability",
    ],
    stats: [
      { value: "11", label: "Core capabilities" },
      { value: "Native", label: "WorkTrack sync" },
      { value: "99.5%", label: "Inventory accuracy" },
    ],
  },
  finance: {
    id: "finance",
    name: "Accounting",
    tagline: "Invoice accurately, track every payment, and stop losing revenue to manual processes.",
    shortDescription:
      "Invoice accurately, track every payment, and stop losing revenue to manual processes. Generate invoices from rate cards and activity data, automate billing triggers, handle partial billing, and give clients clear statements, all with full accounting records.",
    fullDescription:
      "Accounting is built for service businesses, logistics companies, agencies, and professional services firms that bill by activity, contract, or retainer. It generates accurate invoices from rate cards and tracked activity, automates billing with configurable triggers, handles complex partial billing logic, and keeps every payment, receivable, and financial record organized. Revenue dashboards and client statements give you and your customers complete visibility into what has been billed, what is outstanding, and what is coming next.",
    category: "Revenue Management",
    categoryId: "control-execution",
    icon: "ri-coins-line",
    accentImage:
      "https://readdy.ai/api/search-image?query=3D%20isometric%20dark%20mode%20enterprise%20system%20visualization%20of%20a%20financial%20revenue%20dashboard%20with%20flowing%20number%20streams%2C%20invoice%20cards%2C%20rising%20trend%20graphs%2C%20and%20billing%20cycle%20rings%20floating%20in%20a%20layered%20interface%2C%20clean%20minimal%20dark%20graphite%20background%20with%20subtle%20lighting%2C%20technical%20precise%20render%20style%2C%20no%20text%20no%20logos%2C%20no%20people%2C%20subtle%20emerald%20green%20accent%20highlights%20on%20positive%20metrics%2C%20consistent%20lighting%20from%20upper%20left%2C%20sharp%20focus%2C%20high%20detail&width=900&height=500&seq=finance-hero&orientation=landscape",
    features: [
      {
        title: "Invoice Generation",
        description:
          "Create accurate invoices directly from tracked activities, rate cards, and service agreements. Link billing to actual work completed in WorkTrack and OMS for transparent, dispute-free invoicing.",
        icon: "ri-file-list-3-line",
      },
      {
        title: "Rate Card Configuration per Client or Service",
        description:
          "Set custom rate cards for individual clients, service types, or contract tiers. Automatic price application eliminates manual lookups and billing errors.",
        icon: "ri-price-tag-3-line",
      },
      {
        title: "Automated Billing with Activity-Based Triggers",
        description:
          "Trigger invoices automatically when tasks are completed, milestones are reached, or cycles expire. Reduce billing lag and capture revenue faster.",
        icon: "ri-loop-left-line",
      },
      {
        title: "Partial Billing Logic",
        description:
          "Handle complex billing scenarios: partial deliveries, pro-rata periods, split invoices, and progress-based billing, with built-in logic and approval steps.",
        icon: "ri-scissors-cut-line",
      },
      {
        title: "Receivables and Payment Tracking",
        description:
          "Track outstanding invoices, partial payments, overpayments, and aging receivables in real time. Automated reminders improve collection velocity.",
        icon: "ri-hand-coin-line",
      },
      {
        title: "Revenue Dashboards",
        description:
          "Visualize revenue by client, service, period, or location. Spot trends, forecast cash flow, and identify your highest-value engagements at a glance.",
        icon: "ri-bar-chart-box-line",
      },
      {
        title: "Client Statements",
        description:
          "Generate detailed client statements showing invoices, payments, credits, and running balances. Share self-service statements to reduce billing inquiries.",
        icon: "ri-article-line",
      },
      {
        title: "Accounting and Financial Records",
        description:
          "Maintain complete accounting records with full audit trails. Export to your accounting system or use built-in ledger views for period-end reconciliation.",
        icon: "ri-safe-2-line",
      },
    ],
    useCases: [
      {
        title: "Service Businesses",
        description:
          "Bill by the hour, by the job, or by the retainer. Link timesheets and task completion in WorkTrack directly to invoice line items.",
      },
      {
        title: "Logistics Companies",
        description:
          "Invoice by shipment, weight, distance, or storage duration. Rate cards handle complex pricing tiers without manual calculation.",
      },
      {
        title: "Agencies",
        description:
          "Manage recurring retainers, project milestones, and ad-hoc scope changes. Partial billing keeps cash flowing even on long engagements.",
      },
      {
        title: "Professional Services Firms",
        description:
          "Generate clean, detailed invoices from time entries and expense records. Revenue dashboards show practice area profitability and partner performance.",
      },
    ],
    screenshots: [
      {
        label: "Invoice Dashboard",
        image:
          "https://readdy.ai/api/search-image?query=3D%20isometric%20dark%20mode%20enterprise%20invoice%20dashboard%20with%20invoice%20cards%2C%20payment%20status%20badges%2C%20aging%20bars%2C%20and%20send%20buttons%2C%20clean%20minimal%20dark%20graphite%20UI%2C%20technical%20precise%20render%2C%20no%20text%2C%20subtle%20emerald%20green%20accents%2C%20consistent%20lighting%2C%20sharp%20focus%2C%20high%20detail&width=800&height=450&seq=finance-ss1&orientation=landscape",
      },
      {
        label: "Revenue Dashboard",
        image:
          "https://readdy.ai/api/search-image?query=3D%20isometric%20dark%20mode%20enterprise%20revenue%20dashboard%20with%20trend%20lines%2C%20client%20breakdown%20charts%2C%20and%20period%20comparison%20grids%2C%20clean%20minimal%20dark%20graphite%20UI%2C%20technical%20precise%20render%2C%20no%20text%2C%20subtle%20emerald%20green%20accents%2C%20consistent%20lighting%2C%20sharp%20focus%2C%20high%20detail&width=800&height=450&seq=finance-ss2&orientation=landscape",
      },
      {
        label: "Client Statement",
        image:
          "https://readdy.ai/api/search-image?query=3D%20isometric%20dark%20mode%20enterprise%20client%20statement%20interface%20with%20transaction%20rows%2C%20balance%20summaries%2C%20payment%20history%2C%20and%20credit%20notes%2C%20clean%20minimal%20dark%20graphite%20UI%2C%20technical%20precise%20render%2C%20no%20text%2C%20subtle%20emerald%20green%20accents%2C%20consistent%20lighting%2C%20sharp%20focus%2C%20high%20detail&width=800&height=450&seq=finance-ss3&orientation=landscape",
      },
    ],
    integrations: ["WorkTrack", "OMS", "Admin", "HCM"],
    benefits: [
      "Eliminate revenue leakage with activity-linked invoice generation",
      "Automate billing cycles and reduce days-to-invoice",
      "Give clients real-time visibility with self-service statements",
      "Maintain complete financial records with full audit trails",
    ],
    stats: [
      { value: "8", label: "Core capabilities" },
      { value: "Zero", label: "Manual billing errors" },
      { value: "100%", label: "Revenue traceability" },
    ],
  },
  "quality-audit": {
    id: "quality-audit",
    name: "Quality Audit",
    tagline: "Plan, execute, and close audits without the paperwork.",
    shortDescription:
      "Plan, execute, and close audits without the paperwork. Build audit schedules, create custom checklists, track findings and non-conformances, manage corrective actions, and generate compliance reports with full historical records.",
    fullDescription:
      "Quality Audit gives quality managers, compliance officers, and operations leads a structured digital system for every stage of the audit lifecycle. Plan and schedule internal and external audits, build custom checklist templates for any standard or process, execute audits in the field or on the floor, capture findings with severity ratings, track non-conformances through to closure, and manage corrective and preventive actions with built-in escalation. Scoring and compliance reports give leadership instant visibility into quality posture, while the complete audit history ensures you are always ready for certification bodies, regulators, and client reviews. Built for manufacturing, logistics, regulated industries, and ISO-compliant organisations.",
    category: "Compliance & Quality",
    categoryId: "control-execution",
    icon: "ri-shield-check-line",
    accentImage:
      "https://readdy.ai/api/search-image?query=3D%20isometric%20dark%20mode%20enterprise%20system%20visualization%20of%20a%20structured%20quality%20audit%20and%20compliance%20control%20panel%20with%20checklist%20grids%2C%20inspection%20nodes%2C%20status%20indicator%20badges%2C%20non-conformance%20alert%20flags%2C%20and%20scoring%20dashboards%20arranged%20in%20layered%20interface%20cards%2C%20clean%20minimal%20dark%20graphite%20background%20with%20subtle%20lighting%2C%20technical%20precise%20render%20style%2C%20no%20text%20no%20logos%2C%20no%20people%2C%20subtle%20emerald%20green%20accent%20highlights%20on%20compliant%20items%2C%20consistent%20lighting%20from%20upper%20left%2C%20sharp%20focus%2C%20high%20detail&width=900&height=500&seq=qualityaudit-hero&orientation=landscape",
    features: [
      {
        title: "Audit Planning and Scheduling",
        description:
          "Create audit schedules by department, process, location, or compliance standard. Assign auditors, set due dates, and map recurring audit cycles with automatic reminders.",
        icon: "ri-calendar-check-line",
      },
      {
        title: "Checklist Builder",
        description:
          "Build custom audit checklists for any standard: ISO, internal SOP, client requirement, or industry regulation. Reuse templates, version controls, and assign by audit type.",
        icon: "ri-clipboard-line",
      },
      {
        title: "Audit Execution and Findings Tracking",
        description:
          "Run audits in the field or on the floor with digital checklists. Capture findings with photos, notes, and severity ratings in real time. No paper, no lost records.",
        icon: "ri-task-line",
      },
      {
        title: "Non-Conformance and Corrective Action Management",
        description:
          "Log non-conformances instantly, assign owners, set due dates, and track corrective and preventive actions through to closure with full documentation.",
        icon: "ri-error-warning-line",
      },
      {
        title: "Escalation System",
        description:
          "Define escalation rules for overdue findings, high-severity issues, or stalled corrective actions. Route alerts automatically to the right level of management.",
        icon: "ri-arrow-up-circle-line",
      },
      {
        title: "Audit Scoring and Compliance Reports",
        description:
          "Generate scored audit summaries, trend reports, and compliance dashboards. Show leadership quality posture at a glance with historical benchmarking.",
        icon: "ri-bar-chart-box-line",
      },
      {
        title: "Full Audit History",
        description:
          "Every audit, finding, action, and decision is stored with full traceability. Searchable, exportable, and always ready for certification or regulatory review.",
        icon: "ri-history-line",
      },
    ],
    useCases: [
      {
        title: "Manufacturing",
        description:
          "Run process audits, product inspections, and supplier audits on schedule. Track non-conformances from the line to closure with full traceability.",
      },
      {
        title: "Logistics",
        description:
          "Audit warehouse operations, delivery quality, and facility standards. Ensure SLA compliance and client audit readiness.",
      },
      {
        title: "Regulated Industries",
        description:
          "Maintain audit trails for regulatory bodies. Structured checklists, documented findings, and corrective action records reduce compliance risk.",
      },
      {
        title: "ISO-Compliant Organisations",
        description:
          "Align internal audits with ISO 9001, 14001, 45001, and other standards. Scoring, history, and reporting support certification and surveillance audits.",
      },
    ],
    screenshots: [
      {
        label: "Audit Schedule",
        image:
          "https://readdy.ai/api/search-image?query=3D%20isometric%20dark%20mode%20enterprise%20audit%20schedule%20dashboard%20with%20calendar%20timeline%2C%20audit%20cards%2C%20auditor%20assignments%2C%20status%20indicators%2C%20and%20due%20date%20alerts%2C%20clean%20minimal%20dark%20graphite%20UI%2C%20technical%20precise%20render%2C%20no%20text%2C%20subtle%20emerald%20green%20accents%2C%20consistent%20lighting%2C%20sharp%20focus%2C%20high%20detail&width=800&height=450&seq=qualityaudit-ss1&orientation=landscape",
      },
      {
        label: "Findings Board",
        image:
          "https://readdy.ai/api/search-image?query=3D%20isometric%20dark%20mode%20enterprise%20findings%20board%20with%20non-conformance%20cards%2C%20severity%20badges%2C%20corrective%20action%20stages%2C%20and%20owner%20assignments%2C%20clean%20minimal%20dark%20graphite%20UI%2C%20technical%20precise%20render%2C%20no%20text%2C%20subtle%20emerald%20green%20accents%2C%20consistent%20lighting%2C%20sharp%20focus%2C%20high%20detail&width=800&height=450&seq=qualityaudit-ss2&orientation=landscape",
      },
      {
        label: "Compliance Report",
        image:
          "https://readdy.ai/api/search-image?query=3D%20isometric%20dark%20mode%20enterprise%20compliance%20report%20dashboard%20with%20scored%20audit%20summaries%2C%20trend%20graphs%2C%20pass%20rate%20indicators%2C%20and%20historical%20comparison%20panels%2C%20clean%20minimal%20dark%20graphite%20UI%2C%20technical%20precise%20render%2C%20no%20text%2C%20subtle%20emerald%20green%20accents%2C%20consistent%20lighting%2C%20sharp%20focus%2C%20high%20detail&width=800&height=450&seq=qualityaudit-ss3&orientation=landscape",
      },
    ],
    integrations: ["OMS", "WorkTrack", "Admin", "RemoteOps Hub"],
    benefits: [
      "Replace paper checklists and manual logs with structured digital audits",
      "Close non-conformances faster with tracked corrective actions and escalation",
      "Prove compliance instantly with scored reports and full audit history",
      "Reduce certification preparation time with always-ready documentation",
    ],
    stats: [
      { value: "7", label: "Core capabilities" },
      { value: "100%", label: "Audit-ready records" },
      { value: "Zero", label: "Paper dependency" },
    ],
  },
  admin: {
    id: "admin",
    name: "Admin",
    tagline: "Bring structure and full visibility to your internal operations.",
    shortDescription:
      "Bring structure and full visibility to your internal operations. Handle internal requests, manage approvals, track facilities, fleet, and vendors, control assets and documents, monitor visitors, manage HSE, service desk, insurance, and procurement, and keep full administrative logs, all in one place.",
    fullDescription:
      "Admin brings order to the everyday operations that keep your business running. From asset and travel requests to facility, fleet, and vendor management, procurement, HSE, service desk, and insurance tracking, it structures your internal processes with approval workflows, document control, visitor tracking, and complete administrative logs. Request and approval workflows connect natively with WorkTrack task management and HRIS employee records for seamless operational continuity.",
    category: "Internal Operations",
    categoryId: "control-execution",
    icon: "ri-shield-user-line",
    accentImage:
      "https://readdy.ai/api/search-image?query=3D%20isometric%20dark%20mode%20enterprise%20system%20visualization%20of%20a%20system%20control%20panel%20with%20layered%20permission%20matrices%2C%20configuration%20sliders%2C%20role%20hierarchy%20diagrams%2C%20and%20security%20status%20indicators%2C%20clean%20minimal%20dark%20graphite%20background%20with%20subtle%20lighting%2C%20technical%20precise%20render%20style%2C%20no%20text%20no%20logos%2C%20no%20people%2C%20subtle%20emerald%20green%20accent%20highlights%20on%20active%20controls%2C%20consistent%20lighting%20from%20upper%20left%2C%20sharp%20focus%2C%20high%20detail&width=900&height=500&seq=admin-hero&orientation=landscape",
    features: [
      {
        title: "Facility Management",
        description:
          "Track and manage all your facilities, buildings, and workspaces. Schedule maintenance, manage space allocation, and monitor occupancy and condition across every location.",
        icon: "ri-building-line",
      },
      {
        title: "Fleet Management",
        description:
          "Manage your entire vehicle fleet in one place. Track vehicle assignments, maintenance schedules, fuel logs, insurance renewals, and driver allocations with full operational visibility.",
        icon: "ri-car-line",
      },
      {
        title: "Travel Management",
        description:
          "Handle business travel requests, approvals, itineraries, and expense reporting. Track bookings, visas, and travel history with structured workflows from request to reimbursement.",
        icon: "ri-plane-line",
      },
      {
        title: "Procurement Management",
        description:
          "Manage the full procurement lifecycle: requisitions, vendor quotes, purchase orders, approvals, and goods receipt. Control spend with budgets, thresholds, and vendor performance tracking.",
        icon: "ri-shopping-bag-line",
      },
      {
        title: "Asset Management",
        description:
          "Track all physical and digital assets from acquisition through disposal. Manage asset assignments, depreciation, maintenance schedules, warranties, and audit trails in a centralized register.",
        icon: "ri-server-line",
      },
      {
        title: "HSE (Health, Safety & Environment)",
        description:
          "Manage health, safety, and environmental compliance. Track incident reports, safety inspections, risk assessments, training records, and regulatory compliance deadlines with escalation workflows.",
        icon: "ri-first-aid-kit-line",
      },
      {
        title: "Service Desk",
        description:
          "Internal IT and operations support ticketing. Log issues, assign technicians, track resolution times, and maintain a knowledge base for recurring problems and self-service support.",
        icon: "ri-customer-service-2-line",
      },
      {
        title: "Insurance Management",
        description:
          "Track all organizational insurance policies, coverage details, premiums, and renewal dates. Manage claims, documentation, and vendor relationships with automated expiry alerts.",
        icon: "ri-shield-star-line",
      },
      {
        title: "Approvals Workflow",
        description:
          "Define multi-stage approval chains for any internal request. Route to the right approver based on type, amount, or department, with automated reminders and escalation rules.",
        icon: "ri-check-double-line",
      },
      {
        title: "Vendor Management",
        description:
          "Track vendor contracts, service agreements, SLAs, and performance scores. Manage renewals, onboarding, and vendor compliance in a centralized supplier register.",
        icon: "ri-store-2-line",
      },
      {
        title: "Document Control",
        description:
          "Store, version, and control access to administrative documents: policies, SOPs, contracts, and compliance files. Ensure the right people always see the latest version.",
        icon: "ri-folder-shield-line",
      },
      {
        title: "Visitor Tracking",
        description:
          "Log and monitor visitor check-ins, host assignments, and site access. Maintain a digital visitor record for security, compliance, and audit purposes.",
        icon: "ri-user-received-line",
      },
      {
        title: "Administrative Logs",
        description:
          "Keep a complete, searchable record of all internal actions: requests, approvals, changes, and decisions. Full traceability for accountability and review.",
        icon: "ri-file-list-3-line",
      },
    ],
    useCases: [
      {
        title: "Corporate Offices",
        description:
          "Manage internal service requests, office asset allocation, and facility bookings across multiple floors or buildings from a single operations desk.",
      },
      {
        title: "Multi-Site Organizations",
        description:
          "Coordinate facilities, vendors, and administrative processes across branches with localized ownership and centralized oversight.",
      },
      {
        title: "Regulated Workplaces",
        description:
          "Maintain visitor logs, controlled document access, and audit-ready administrative records for compliance with safety and security standards.",
      },
      {
        title: "Growing Teams",
        description:
          "Scale internal operations without chaos. Structured requests and approvals keep administrative overhead manageable as headcount grows.",
      },
    ],
    screenshots: [
      {
        label: "Request Dashboard",
        image:
          "https://readdy.ai/api/search-image?query=3D%20isometric%20dark%20mode%20enterprise%20request%20dashboard%20with%20pending%20cards%2C%20approval%20stages%2C%20status%20badges%2C%20and%20requester%20info%2C%20clean%20minimal%20dark%20graphite%20UI%2C%20technical%20precise%20render%2C%20no%20text%2C%20subtle%20emerald%20green%20accents%2C%20consistent%20lighting%2C%20sharp%20focus%2C%20high%20detail&width=800&height=450&seq=admin-ss1&orientation=landscape",
      },
      {
        label: "Approval Workflow",
        image:
          "https://readdy.ai/api/search-image?query=3D%20isometric%20dark%20mode%20enterprise%20approval%20workflow%20interface%20with%20chain%20stages%2C%20approver%20avatars%2C%20decision%20buttons%2C%20and%20escalation%20timeline%2C%20clean%20minimal%20dark%20graphite%20UI%2C%20technical%20precise%20render%2C%20no%20text%2C%20subtle%20emerald%20green%20accents%2C%20consistent%20lighting%2C%20sharp%20focus%2C%20high%20detail&width=800&height=450&seq=admin-ss2&orientation=landscape",
      },
      {
        label: "Facility Overview",
        image:
          "https://readdy.ai/api/search-image?query=3D%20isometric%20dark%20mode%20enterprise%20facility%20overview%20with%20building%20nodes%2C%20vendor%20cards%2C%20maintenance%20calendars%2C%20and%20service%20status%20indicators%2C%20clean%20minimal%20dark%20graphite%20UI%2C%20technical%20precise%20render%2C%20no%20text%2C%20subtle%20emerald%20green%20accents%2C%20consistent%20lighting%2C%20sharp%20focus%2C%20high%20detail&width=800&height=450&seq=admin-ss3&orientation=landscape",
      },
    ],
    integrations: ["WorkTrack", "HCM", "PMS", "Finance"],
    benefits: [
      "Streamline internal requests with structured digital workflows",
      "Connect approvals natively with WorkTrack tasks and HRIS records",
      "Maintain full visibility into facilities, vendors, and visitors",
      "Keep complete administrative logs for accountability and audit",
    ],
    stats: [
      { value: "12", label: "Core capabilities" },
      { value: "Native", label: "WorkTrack + HRIS sync" },
      { value: "100%", label: "Audit-ready logs" },
    ],
  },
  learn: {
    id: "learn",
    name: "Learning & Development",
    tagline: "Build the skills your business needs inside the platform your team already uses.",
    shortDescription:
      "Build the skills your business needs inside the platform your team already uses. Deliver courses, track certifications, run assessments, and build a culture of continuous learning.",
    fullDescription:
      "Learning & Development turns training from a manual, spreadsheet-driven process into a structured digital program. Create courses with video, documents, and modules, assign them to roles or individuals, track completion and scores, and manage certificate generation, all while linking learning outcomes to performance in PMS.",
    category: "Training & Knowledge",
    categoryId: "people-performance",
    icon: "ri-graduation-cap-line",
    accentImage:
      "https://readdy.ai/api/search-image?query=3D%20isometric%20dark%20mode%20enterprise%20system%20visualization%20of%20a%20structured%20learning%20progression%20system%20with%20connected%20knowledge%20nodes%2C%20certification%20tracks%2C%20skill%20tree%20layers%2C%20and%20course%20module%20pipelines%20flowing%20through%20a%20grid%2C%20clean%20minimal%20dark%20graphite%20background%20with%20subtle%20lighting%2C%20technical%20precise%20render%20style%2C%20no%20text%20no%20logos%2C%20no%20people%2C%20subtle%20emerald%20green%20accent%20highlights%20on%20completed%20pathways%2C%20consistent%20lighting%20from%20upper%20left%2C%20sharp%20focus%2C%20high%20detail&width=900&height=500&seq=learn-hero&orientation=landscape",
    features: [
      {
        title: "Course Creation: Video, Documents, and Modules",
        description:
          "Build structured courses from videos, documents, and interactive modules. Organize content into sequenced sections with prerequisites, time estimates, and completion rules.",
        icon: "ri-book-open-line",
      },
      {
        title: "Onboarding Training Programmes",
        description:
          "Design dedicated onboarding tracks for new hires. Automatically enroll fresh starters into role-specific orientation, compliance, and culture modules from day one.",
        icon: "ri-user-add-line",
      },
      {
        title: "Mandatory Course Assignment by Role or Department",
        description:
          "Assign required courses by job role, department, or seniority level. Ensure every team member completes the training relevant to their position with automated enrollment and reminders.",
        icon: "ri-group-line",
      },
      {
        title: "Quizzes and Assessments",
        description:
          "Create multiple-choice, true/false, and open-ended quizzes. Set pass thresholds, retry limits, and timed assessments to validate knowledge retention and skill mastery.",
        icon: "ri-questionnaire-line",
      },
      {
        title: "Progress Tracking and Completion Reports",
        description:
          "Monitor completion rates, average scores, time-to-finish, and overdue assignments by individual, team, or department. Spot skill gaps and training bottlenecks instantly.",
        icon: "ri-bar-chart-horizontal-line",
      },
      {
        title: "Certificate Generation",
        description:
          "Issue digital certificates automatically on course completion. Track certificate expiry dates, renewal requirements, and recertification status for compliance and audit readiness.",
        icon: "ri-medal-line",
      },
      {
        title: "Staff Learning Dashboard",
        description:
          "Give every employee a personal learning dashboard showing assigned courses, progress, upcoming deadlines, and earned certificates. Empower self-directed development in one view.",
        icon: "ri-dashboard-3-line",
      },
    ],
    useCases: [
      {
        title: "New Employee Onboarding",
        description:
          "Structured day-one through week-four programs with compliance modules, culture sessions, and role-specific training.",
      },
      {
        title: "Compliance & Certification",
        description:
          "Mandatory annual training with automatic re-enrollment, expiry alerts, and audit-ready completion records.",
      },
      {
        title: "Sales & Product Training",
        description:
          "Keep sales teams current on product updates, competitive positioning, and pitch certification with quick micro-courses.",
      },
      {
        title: "Leadership Development",
        description:
          "Multi-track programs for emerging managers with assessments, peer feedback, and capstone projects.",
      },
    ],
    screenshots: [
      {
        label: "Course Catalog",
        image:
          "https://readdy.ai/api/search-image?query=3D%20isometric%20dark%20mode%20enterprise%20course%20catalog%20with%20course%20cards%2C%20progress%20rings%2C%20category%20filters%2C%20and%20enrollment%20buttons%2C%20clean%20minimal%20dark%20graphite%20UI%2C%20technical%20precise%20render%2C%20no%20text%2C%20subtle%20emerald%20green%20accents%2C%20consistent%20lighting%2C%20sharp%20focus%2C%20high%20detail&width=800&height=450&seq=learn-ss1&orientation=landscape",
      },
      {
        label: "Learning Path",
        image:
          "https://readdy.ai/api/search-image?query=3D%20isometric%20dark%20mode%20enterprise%20learning%20path%20visualization%20with%20connected%20module%20nodes%2C%20completion%20checkmarks%2C%20and%20locked%20stage%20indicators%2C%20clean%20minimal%20dark%20graphite%20UI%2C%20technical%20precise%20render%2C%20no%20text%2C%20subtle%20emerald%20green%20accents%2C%20consistent%20lighting%2C%20sharp%20focus%2C%20high%20detail&width=800&height=450&seq=learn-ss2&orientation=landscape",
      },
      {
        label: "Certification Wall",
        image:
          "https://readdy.ai/api/search-image?query=3D%20isometric%20dark%20mode%20enterprise%20certification%20wall%20with%20badge%20cards%2C%20expiry%20dates%2C%20renewal%20status%2C%20and%20skill%20labels%2C%20clean%20minimal%20dark%20graphite%20UI%2C%20technical%20precise%20render%2C%20no%20text%2C%20subtle%20emerald%20green%20accents%2C%20consistent%20lighting%2C%20sharp%20focus%2C%20high%20detail&width=800&height=450&seq=learn-ss3&orientation=landscape",
      },
    ],
    integrations: ["HCM", "PMS", "Admin"],
    benefits: [
      "Reduce onboarding time by 40% with structured programs",
      "Ensure 100% compliance completion with automated tracking",
      "Link learning to performance and career progression",
      "Scale training across distributed teams without travel",
    ],
    stats: [
      { value: "7", label: "Core capabilities" },
      { value: "100%", label: "Compliance coverage" },
      { value: "Unlimited", label: "Course scaling" },
    ],
  },
  "remoteops-hub": {
    id: "remoteops-hub",
    name: "RemoteOps Hub",
    tagline: "Command center for distributed operations",
    shortDescription:
      "Monitor multiple locations, teams, and systems from a single real-time dashboard built for remote oversight.",
    fullDescription:
      "RemoteOps Hub is the nerve center for businesses running distributed operations. It aggregates data from WorkTrack, OMS, Quality Audit, and Finance across all your locations into a unified command view. See what's happening where, identify issues before they escalate, and coordinate responses without being on-site.",
    category: "Control & Execution",
    categoryId: "control-execution",
    icon: "ri-remote-control-line",
    accentImage:
      "https://readdy.ai/api/search-image?query=3D%20isometric%20dark%20mode%20enterprise%20system%20visualization%20of%20a%20global%20operations%20coordination%20network%20with%20a%20world%20map%20grid%2C%20distributed%20team%20nodes%20connected%20by%20glowing%20coordination%20lines%2C%20real-time%20status%20hubs%2C%20and%20synchronized%20system%20layers%2C%20clean%20minimal%20dark%20graphite%20background%20with%20subtle%20lighting%2C%20technical%20precise%20render%20style%2C%20no%20text%20no%20logos%2C%20no%20people%2C%20subtle%20emerald%20green%20accent%20highlights%20on%20active%20connections%2C%20consistent%20lighting%20from%20upper%20left%2C%20sharp%20focus%2C%20high%20detail&width=900&height=500&seq=remoteops-hero&orientation=landscape",
    features: [
      {
        title: "Multi-Location Dashboard",
        description:
          "Real-time status tiles for every branch, warehouse, or site. See active tasks, inventory alerts, and financial summaries at a glance.",
        icon: "ri-layout-grid-line",
      },
      {
        title: "Live Operations Feed",
        description:
          "Streaming event log from all connected modules. Filter by location, severity, or module. Get notified on exceptions.",
        icon: "ri-live-line",
      },
      {
        title: "Cross-Location Analytics",
        description:
          "Compare performance, costs, and output across locations. Identify best practices and underperformers with normalized metrics.",
        icon: "ri-bar-chart-grouped-line",
      },
      {
        title: "Incident & Alert Management",
        description:
          "Auto-detect anomalies and create incident tickets. Assign response owners, track resolution, and post-incident review.",
        icon: "ri-alarm-warning-line",
      },
      {
        title: "Resource Coordination",
        description:
          "Reallocate staff, inventory, or equipment across locations based on real-time demand signals and capacity.",
        icon: "ri-exchange-line",
      },
      {
        title: "Executive Summary Reports",
        description:
          "Automated daily, weekly, and monthly summaries across all sites. Email-ready format with key metrics and trend highlights.",
        icon: "ri-file-chart-line",
      },
    ],
    useCases: [
      {
        title: "Retail Chain Operations",
        description:
          "Monitor sales, stock, and staff across hundreds of stores from headquarters. Respond to regional trends in real time.",
      },
      {
        title: "Logistics Networks",
        description:
          "Track hub performance, route efficiency, and delivery status across a network of distribution centers.",
      },
      {
        title: "Security & Facility Management",
        description:
          "Oversee patrols, incidents, and maintenance across client sites from a central operations room or mobile command.",
      },
      {
        title: "Manufacturing with Multiple Plants",
        description:
          "Compare OEE, quality, and downtime across plants. Standardize best practices and coordinate maintenance windows.",
      },
    ],
    screenshots: [
      {
        label: "Command Dashboard",
        image:
          "https://readdy.ai/api/search-image?query=3D%20isometric%20dark%20mode%20enterprise%20command%20dashboard%20with%20location%20tiles%2C%20status%20indicators%2C%20alert%20banners%2C%20and%20live%20metrics%2C%20clean%20minimal%20dark%20graphite%20UI%2C%20technical%20precise%20render%2C%20no%20text%2C%20subtle%20emerald%20green%20accents%2C%20consistent%20lighting%2C%20sharp%20focus%2C%20high%20detail&width=800&height=450&seq=remoteops-ss1&orientation=landscape",
      },
      {
        label: "Operations Map",
        image:
          "https://readdy.ai/api/search-image?query=3D%20isometric%20dark%20mode%20enterprise%20operations%20map%20with%20site%20markers%2C%20status%20colors%2C%20connection%20lines%2C%20and%20regional%20overlays%2C%20clean%20minimal%20dark%20graphite%20UI%2C%20technical%20precise%20render%2C%20no%20text%2C%20subtle%20emerald%20green%20accents%2C%20consistent%20lighting%2C%20sharp%20focus%2C%20high%20detail&width=800&height=450&seq=remoteops-ss2&orientation=landscape",
      },
      {
        label: "Incident Feed",
        image:
          "https://readdy.ai/api/search-image?query=3D%20isometric%20dark%20mode%20enterprise%20incident%20feed%20with%20event%20cards%2C%20severity%20badges%2C%20assignee%20avatars%2C%20and%20resolution%20timelines%2C%20clean%20minimal%20dark%20graphite%20UI%2C%20technical%20precise%20render%2C%20no%20text%2C%20subtle%20emerald%20green%20accents%2C%20consistent%20lighting%2C%20sharp%20focus%2C%20high%20detail&width=800&height=450&seq=remoteops-ss3&orientation=landscape",
      },
    ],
    integrations: ["WorkTrack", "OMS", "Finance", "PMS", "HCM", "Quality Audit"],
    benefits: [
      "Eliminate information silos between locations",
      "Reduce response time to operational issues by 60%",
      "Make data-driven decisions with cross-site visibility",
      "Scale oversight without proportional headcount increase",
    ],
    stats: [
      { value: "60%", label: "Faster response" },
      { value: "100%", label: "Location coverage" },
      { value: "Unified", label: "Command view" },
    ],
  },
  "growth-marketing": {
    id: "growth-marketing",
    name: "Growth & Marketing",
    tagline: "Drive awareness, generate demand, and execute campaigns that convert prospects into loyal customers.",
    shortDescription:
      "Drive awareness, generate demand, and execute campaigns that convert prospects into loyal customers. The Growth & Marketing module helps businesses plan, execute, and measure marketing activities from a single platform.",
    fullDescription:
      "Growth & Marketing gives marketing teams, growth managers, and business owners a unified platform to plan campaigns, create and schedule content, capture leads, run email and SMS marketing, segment audiences, and measure performance. With AI-assisted content suggestions and integrated task management, your marketing engine runs smarter and faster, directly feeding qualified leads into the Sales & CRM pipeline.",
    category: "Marketing & Demand Generation",
    categoryId: "growth-marketing",
    icon: "ri-megaphone-line",
    accentImage:
      "https://readdy.ai/api/search-image?query=3D%20isometric%20dark%20mode%20enterprise%20system%20visualization%20of%20a%20marketing%20growth%20engine%20with%20campaign%20funnels%2C%20content%20calendar%20grids%2C%20audience%20segment%20nodes%20connected%20by%20data%20flow%20lines%2C%20email%20and%20SMS%20channel%20icons%2C%20performance%20dashboards%20with%20upward%20trend%20indicators%2C%20clean%20minimal%20dark%20graphite%20background%20with%20subtle%20lighting%2C%20technical%20precise%20render%20style%2C%20no%20text%20no%20logos%2C%20no%20people%2C%20subtle%20emerald%20green%20accent%20highlights%20on%20conversion%20points%2C%20consistent%20lighting%20from%20upper%20left%2C%20sharp%20focus%2C%20high%20detail&width=900&height=500&seq=growthmarketing-hero&orientation=landscape",
    features: [
      {
        title: "Campaign Management",
        description:
          "Plan, launch, and track multi-channel campaigns from a single dashboard. Set objectives, timelines, budgets, and KPIs, then monitor performance as campaigns run across email, social, and web channels.",
        icon: "ri-flag-line",
      },
      {
        title: "Content Planner & Calendar",
        description:
          "Organize all content creation in a visual calendar. Schedule blog posts, social updates, email newsletters, and landing pages with team assignments, deadlines, and approval workflows built in.",
        icon: "ri-calendar-event-line",
      },
      {
        title: "Lead Capture Forms",
        description:
          "Build and embed customizable lead capture forms without code. Connect forms to landing pages, track conversion rates, and automatically route new leads into the CRM pipeline for follow-up.",
        icon: "ri-survey-line",
      },
      {
        title: "Email & SMS Marketing",
        description:
          "Create and send targeted email and SMS campaigns. Use drag-and-drop email builders, template libraries, personalization tokens, and A/B testing to optimize open rates, click-throughs, and conversions.",
        icon: "ri-mail-send-line",
      },
      {
        title: "Audience Segmentation",
        description:
          "Segment your audience based on behavior, demographics, engagement history, and custom attributes. Target the right message to the right people at the right time with dynamic list building.",
        icon: "ri-pie-chart-line",
      },
      {
        title: "Marketing Performance Analytics",
        description:
          "Track campaign ROI, channel performance, cost per lead, conversion rates, and customer acquisition cost in real-time dashboards. Make data-driven budget allocation decisions with confidence.",
        icon: "ri-bar-chart-box-line",
      },
      {
        title: "Marketing Task Management",
        description:
          "Assign tasks, track deadlines, and manage marketing workflows with built-in project management. From content drafts to campaign go-live, keep the entire marketing team aligned and accountable.",
        icon: "ri-task-line",
      },
      {
        title: "AI-Assisted Content Suggestions",
        description:
          "Get intelligent content recommendations based on audience engagement patterns, trending topics, and past performance. AI suggests headlines, copy variations, and optimal send times to maximize impact.",
        icon: "ri-robot-line",
      },
    ],
    useCases: [
      {
        title: "Product Launches",
        description:
          "Orchestrate full go-to-market campaigns with coordinated content, email sequences, lead capture, and performance tracking from pre-launch teasers through post-launch follow-up.",
      },
      {
        title: "Lead Generation Programs",
        description:
          "Build multi-touch lead generation funnels with gated content, webinar registration, email nurture sequences, and automated CRM handoff for qualified leads.",
      },
      {
        title: "Content Marketing Operations",
        description:
          "Plan quarterly content calendars, manage writer assignments, track publishing schedules, and measure content ROI across blogs, social, and email channels.",
      },
      {
        title: "Customer Retention Campaigns",
        description:
          "Design segmented re-engagement campaigns, loyalty program communications, and win-back sequences with automated triggers based on customer behavior signals.",
      },
    ],
    screenshots: [
      {
        label: "Campaign Dashboard",
        image:
          "https://readdy.ai/api/search-image?query=3D%20isometric%20dark%20mode%20enterprise%20campaign%20dashboard%20with%20campaign%20cards%2C%20performance%20gauges%2C%20channel%20breakdown%20charts%2C%20and%20conversion%20funnels%2C%20clean%20minimal%20dark%20graphite%20UI%2C%20technical%20precise%20render%2C%20no%20text%2C%20subtle%20emerald%20green%20accents%2C%20consistent%20lighting%2C%20sharp%20focus%2C%20high%20detail&width=800&height=450&seq=growthmarketing-ss1&orientation=landscape",
      },
      {
        label: "Content Calendar",
        image:
          "https://readdy.ai/api/search-image?query=3D%20isometric%20dark%20mode%20enterprise%20content%20calendar%20with%20monthly%20grid%2C%20content%20cards%20by%20date%2C%20status%20indicators%2C%20and%20team%20assignment%20avatars%2C%20clean%20minimal%20dark%20graphite%20UI%2C%20technical%20precise%20render%2C%20no%20text%2C%20subtle%20emerald%20green%20accents%2C%20consistent%20lighting%2C%20sharp%20focus%2C%20high%20detail&width=800&height=450&seq=growthmarketing-ss2&orientation=landscape",
      },
      {
        label: "Analytics Overview",
        image:
          "https://readdy.ai/api/search-image?query=3D%20isometric%20dark%20mode%20enterprise%20marketing%20analytics%20with%20trend%20graphs%2C%20channel%20comparison%20bars%2C%20conversion%20rate%20gauges%2C%20and%20ROI%20summary%20cards%2C%20clean%20minimal%20dark%20graphite%20UI%2C%20technical%20precise%20render%2C%20no%20text%2C%20subtle%20emerald%20green%20accents%2C%20consistent%20lighting%2C%20sharp%20focus%2C%20high%20detail&width=800&height=450&seq=growthmarketing-ss3&orientation=landscape",
      },
    ],
    integrations: ["Sales & CRM", "HCM", "Learn", "Finance"],
    benefits: [
      "Unify all marketing activities in a single platform instead of juggling five different tools",
      "Capture and qualify leads with forms that feed directly into the CRM pipeline",
      "Make smarter budget decisions with real-time campaign ROI and channel analytics",
      "Accelerate content production with AI-assisted suggestions and structured task workflows",
    ],
    stats: [
      { value: "8", label: "Core capabilities" },
      { value: "Unified", label: "Marketing platform" },
      { value: "AI-powered", label: "Content engine" },
    ],
  },
  "sales-crm": {
    id: "sales-crm",
    name: "Sales & CRM",
    tagline: "Build stronger customer relationships and manage your sales pipeline with confidence.",
    shortDescription:
      "Build stronger customer relationships and manage your sales pipeline with confidence. The Sales & CRM module provides complete visibility into your customer journey from lead generation to deal closure and account management.",
    fullDescription:
      "Sales & CRM gives sales teams, account managers, and business development professionals a complete system to manage leads, track opportunities through the pipeline, maintain contact and account records, log sales activities, generate quotations and proposals, and forecast revenue with confidence. With built-in team collaboration tools and native integration with Growth & Marketing, every qualified lead flows seamlessly into the sales process and every closed deal creates a structured handoff to operations and finance.",
    category: "Sales Pipeline & Customer Relationships",
    categoryId: "sales-crm",
    icon: "ri-exchange-dollar-line",
    accentImage:
      "https://readdy.ai/api/search-image?query=3D%20isometric%20dark%20mode%20enterprise%20system%20visualization%20of%20a%20sales%20CRM%20pipeline%20with%20deal%20cards%20flowing%20through%20pipeline%20stages%2C%20revenue%20forecast%20charts%2C%20contact%20relationship%20web%20with%20connected%20nodes%2C%20quotation%20document%20icons%2C%20and%20performance%20dashboard%20gauges%2C%20clean%20minimal%20dark%20graphite%20background%20with%20subtle%20lighting%2C%20technical%20precise%20render%20style%2C%20no%20text%20no%20logos%2C%20no%20people%2C%20subtle%20emerald%20green%20accent%20highlights%20on%20won%20deals%2C%20consistent%20lighting%20from%20upper%20left%2C%20sharp%20focus%2C%20high%20detail&width=900&height=500&seq=salescrm-hero&orientation=landscape",
    features: [
      {
        title: "Lead Management",
        description:
          "Capture leads from Growth & Marketing forms, manual entry, and imports. Qualify, score, and assign leads automatically based on custom rules. Track source attribution and conversion rates for every channel.",
        icon: "ri-user-add-line",
      },
      {
        title: "Opportunity & Pipeline Tracking",
        description:
          "Visualize your entire sales pipeline with drag-and-drop deal stages. Track deal value, probability, expected close dates, and next steps. Get pipeline health snapshots and identify bottlenecks before they cost you deals.",
        icon: "ri-git-branch-line",
      },
      {
        title: "Contact & Account Management",
        description:
          "Maintain detailed profiles for every contact and account. Track communication history, deal history, documents, notes, and relationship mapping. Know exactly who you're talking to and what matters to them.",
        icon: "ri-contacts-book-line",
      },
      {
        title: "Sales Activity Tracking",
        description:
          "Log calls, meetings, emails, and tasks against contacts and deals. Set reminders, schedule follow-ups, and maintain complete activity timelines so nothing falls through the cracks.",
        icon: "ri-phone-line",
      },
      {
        title: "Quotations & Proposals",
        description:
          "Generate professional quotations and proposals with product catalogs, pricing tables, and terms. Track quote status, version history, and conversion from quote to order with e-signature readiness.",
        icon: "ri-file-text-line",
      },
      {
        title: "Customer Communication History",
        description:
          "Every email, call note, meeting summary, and proposal is logged against the contact and account. Build a complete communication timeline that the entire team can reference for context.",
        icon: "ri-message-3-line",
      },
      {
        title: "Sales Performance Dashboards",
        description:
          "Track individual and team performance against quotas. Monitor win rates, average deal size, sales cycle length, and pipeline velocity. Identify top performers and coaching opportunities in real time.",
        icon: "ri-dashboard-line",
      },
      {
        title: "Forecasting & Reporting",
        description:
          "Generate weighted and unweighted revenue forecasts based on pipeline stage probabilities. Run reports on deal trends, team performance, product mix, and customer acquisition costs for leadership reviews.",
        icon: "ri-line-chart-line",
      },
      {
        title: "Team Collaboration Tools",
        description:
          "Share deal notes, tag teammates, assign tasks, and collaborate on proposals in real time. Keep the entire sales team aligned without switching between apps or losing context.",
        icon: "ri-team-line",
      },
    ],
    useCases: [
      {
        title: "B2B Sales Teams",
        description:
          "Manage complex, multi-stakeholder deals with long sales cycles. Track touchpoints across buying committees and forecast revenue with stage-weighted probability models.",
      },
      {
        title: "Account Management",
        description:
          "Maintain comprehensive account profiles, track renewal dates, upsell opportunities, and account health scores. Never miss a renewal or expansion conversation.",
      },
      {
        title: "Inside Sales & SDR Teams",
        description:
          "Process high volumes of inbound leads with automated assignment, activity logging, and pipeline progression. Measure outreach effectiveness and conversion at every stage.",
      },
      {
        title: "Field Sales Operations",
        description:
          "Mobile-first access to pipeline, contacts, and activity logging. Log calls and meetings from the field, access deal history on the go, and stay synced with the office.",
      },
    ],
    screenshots: [
      {
        label: "Pipeline Board",
        image:
          "https://readdy.ai/api/search-image?query=3D%20isometric%20dark%20mode%20enterprise%20sales%20pipeline%20kanban%20board%20with%20deal%20cards%20across%20stages%2C%20value%20indicators%2C%20probability%20badges%2C%20and%20close%20date%20labels%2C%20clean%20minimal%20dark%20graphite%20UI%2C%20technical%20precise%20render%2C%20no%20text%2C%20subtle%20emerald%20green%20accents%2C%20consistent%20lighting%2C%20sharp%20focus%2C%20high%20detail&width=800&height=450&seq=salescrm-ss1&orientation=landscape",
      },
      {
        label: "Contact Timeline",
        image:
          "https://readdy.ai/api/search-image?query=3D%20isometric%20dark%20mode%20enterprise%20contact%20profile%20with%20timeline%20feed%20of%20calls%20meetings%20emails%20and%20notes%2C%20deal%20history%20cards%2C%20and%20relationship%20mapping%2C%20clean%20minimal%20dark%20graphite%20UI%2C%20technical%20precise%20render%2C%20no%20text%2C%20subtle%20emerald%20green%20accents%2C%20consistent%20lighting%2C%20sharp%20focus%2C%20high%20detail&width=800&height=450&seq=salescrm-ss2&orientation=landscape",
      },
      {
        label: "Forecast Report",
        image:
          "https://readdy.ai/api/search-image?query=3D%20isometric%20dark%20mode%20enterprise%20revenue%20forecast%20with%20bar%20charts%2C%20pipeline%20waterfall%2C%20quota%20attainment%20gauges%2C%20and%20trend%20projections%2C%20clean%20minimal%20dark%20graphite%20UI%2C%20technical%20precise%20render%2C%20no%20text%2C%20subtle%20emerald%20green%20accents%2C%20consistent%20lighting%2C%20sharp%20focus%2C%20high%20detail&width=800&height=450&seq=salescrm-ss3&orientation=landscape",
      },
    ],
    integrations: ["Growth & Marketing", "Finance", "HCM", "WorkTrack", "OMS"],
    benefits: [
      "Eliminate scattered spreadsheets and siloed deal tracking with a single pipeline view",
      "Convert more leads with automated handoff from marketing campaigns to sales follow-up",
      "Forecast revenue accurately with stage-weighted pipeline models and historical trend data",
      "Give every team member complete customer context with unified communication timelines",
    ],
    stats: [
      { value: "9", label: "Core capabilities" },
      { value: "Full", label: "Pipeline visibility" },
      { value: "Native", label: "Marketing sync" },
    ],
  },
  "workplace-services": {
    id: "workplace-services",
    name: "Workplace Services",
    tagline: "Create a more organized, efficient, and employee-friendly workplace.",
    shortDescription:
      "Create a more organized, efficient, and employee-friendly workplace. The Workplace Services module streamlines internal service requests and administrative processes to improve employee experience and operational efficiency.",
    fullDescription:
      "Workplace Services transforms how organizations handle internal service delivery. From facility maintenance requests and meeting room bookings to visitor management, asset allocation, transportation requests, and helpdesk ticketing, every workplace service runs through a structured digital workflow. Employees submit requests through a self-service portal, managers approve with context, and facilities teams track everything with service level monitoring and reporting. The result is a more organized workplace, happier employees, and complete operational visibility for workplace managers.",
    category: "Internal Services & Facilities",
    categoryId: "workplace-services",
    icon: "ri-building-4-line",
    accentImage:
      "https://readdy.ai/api/search-image?query=3D%20isometric%20dark%20mode%20enterprise%20system%20visualization%20of%20a%20workplace%20services%20management%20hub%20with%20facility%20layout%20cards%2C%20meeting%20room%20booking%20grids%2C%20service%20request%20ticket%20flows%2C%20maintenance%20schedule%20timelines%2C%20visitor%20check-in%20nodes%2C%20and%20asset%20allocation%20dashboards%2C%20clean%20minimal%20dark%20graphite%20background%20with%20subtle%20lighting%2C%20technical%20precise%20render%20style%2C%20no%20text%20no%20logos%2C%20no%20people%2C%20subtle%20emerald%20green%20accent%20highlights%20on%20active%20requests%2C%20consistent%20lighting%20from%20upper%20left%2C%20sharp%20focus%2C%20high%20detail&width=900&height=500&seq=workplaceservices-hero&orientation=landscape",
    features: [
      {
        title: "Service Request Management",
        description:
          "Centralized intake for all internal service requests. Employees submit requests through a self-service portal, managers approve with one click, and service teams receive structured tickets with priorities, categories, and SLAs attached.",
        icon: "ri-clipboard-line",
      },
      {
        title: "Facility & Maintenance Requests",
        description:
          "Report and track facility issues from leaky faucets to HVAC failures. Categorize by urgency, assign to maintenance teams, track resolution progress, and maintain a complete maintenance history for every asset and space.",
        icon: "ri-tools-line",
      },
      {
        title: "Visitor Management",
        description:
          "Pre-register visitors, generate digital passes, notify hosts on arrival, and maintain a searchable visitor log. Streamline reception workflows and ensure security compliance with complete visitor tracking.",
        icon: "ri-user-received-line",
      },
      {
        title: "Meeting Room Booking",
        description:
          "Browse available rooms by capacity, equipment, and location. Book rooms directly with calendar integration, manage recurring reservations, and handle conflicts with smart scheduling suggestions.",
        icon: "ri-door-open-line",
      },
      {
        title: "Asset Requests & Allocation",
        description:
          "Request laptops, monitors, furniture, tools, and other workplace assets through structured workflows. Track allocation, returns, asset condition, and availability in a centralized asset register.",
        icon: "ri-computer-line",
      },
      {
        title: "Transportation Requests",
        description:
          "Book company vehicles, shuttle services, or external transport for business needs. Manage driver assignments, route planning, pick-up schedules, and track trip completion with integrated approval chains.",
        icon: "ri-car-line",
      },
      {
        title: "Helpdesk & Ticketing",
        description:
          "Full-service internal helpdesk for IT, HR, facilities, and admin queries. Create, assign, prioritize, and track tickets through to resolution with automated escalation, SLAs, and satisfaction surveys.",
        icon: "ri-customer-service-2-line",
      },
      {
        title: "Workplace Announcements",
        description:
          "Broadcast workplace-wide announcements for policy updates, facility notices, event invitations, and emergency alerts. Target by location, department, or role with read receipts and acknowledgment tracking.",
        icon: "ri-megaphone-line",
      },
      {
        title: "Service Level Monitoring & Reporting",
        description:
          "Track response times, resolution rates, SLA compliance, and employee satisfaction across all service categories. Identify bottlenecks, optimize resource allocation, and report workplace operations performance to leadership.",
        icon: "ri-bar-chart-box-line",
      },
    ],
    useCases: [
      {
        title: "Corporate Headquarters",
        description:
          "Manage hundreds of daily service requests across multiple floors and departments. From desk moves to AV setup, keep the workplace running smoothly with structured workflows.",
      },
      {
        title: "Multi-Site Organizations",
        description:
          "Standardize workplace service delivery across branches, warehouses, and regional offices. Centralized oversight with localized service teams and site-specific SLAs.",
      },
      {
        title: "Co-Working & Flexible Spaces",
        description:
          "Manage meeting room bookings, visitor check-ins, asset requests, and member services across shared spaces with self-service portals and automated workflows.",
      },
      {
        title: "Manufacturing Facilities",
        description:
          "Coordinate maintenance requests, safety equipment allocation, visitor management for plant tours, and internal transport logistics across large industrial sites.",
      },
    ],
    screenshots: [
      {
        label: "Service Dashboard",
        image:
          "https://readdy.ai/api/search-image?query=3D%20isometric%20dark%20mode%20enterprise%20service%20dashboard%20with%20request%20cards%20by%20category%2C%20status%20indicators%2C%20SLA%20timers%2C%20and%20priority%20badges%2C%20clean%20minimal%20dark%20graphite%20UI%2C%20technical%20precise%20render%2C%20no%20text%2C%20subtle%20emerald%20green%20accents%2C%20consistent%20lighting%2C%20sharp%20focus%2C%20high%20detail&width=800&height=450&seq=workplaceservices-ss1&orientation=landscape",
      },
      {
        label: "Room Booking",
        image:
          "https://readdy.ai/api/search-image?query=3D%20isometric%20dark%20mode%20enterprise%20room%20booking%20interface%20with%20floor%20plan%20grids%2C%20time%20slot%20selectors%2C%20room%20cards%20with%20capacity%20and%20equipment%20icons%2C%20and%20calendar%20overlays%2C%20clean%20minimal%20dark%20graphite%20UI%2C%20technical%20precise%20render%2C%20no%20text%2C%20subtle%20emerald%20green%20accents%2C%20consistent%20lighting%2C%20sharp%20focus%2C%20high%20detail&width=800&height=450&seq=workplaceservices-ss2&orientation=landscape",
      },
      {
        label: "SLA Report",
        image:
          "https://readdy.ai/api/search-image?query=3D%20isometric%20dark%20mode%20enterprise%20SLA%20compliance%20report%20with%20response%20time%20gauges%2C%20resolution%20rate%20charts%2C%20satisfaction%20scorecards%2C%20and%20category%20breakdown%20bars%2C%20clean%20minimal%20dark%20graphite%20UI%2C%20technical%20precise%20render%2C%20no%20text%2C%20subtle%20emerald%20green%20accents%2C%20consistent%20lighting%2C%20sharp%20focus%2C%20high%20detail&width=800&height=450&seq=workplaceservices-ss3&orientation=landscape",
      },
    ],
    integrations: ["Admin", "HCM", "WorkTrack", "Finance"],
    benefits: [
      "Replace email chains and hallway requests with structured digital service workflows",
      "Give employees a single self-service portal for every workplace need",
      "Track and optimize service delivery with real-time SLA monitoring and reporting",
      "Improve workplace satisfaction scores with faster, more transparent service resolution",
    ],
    stats: [
      { value: "9", label: "Core capabilities" },
      { value: "Self-service", label: "Employee portal" },
      { value: "100%", label: "SLA visibility" },
    ],
  },
  "get-fit": {
    id: "get-fit",
    name: "Get Fit",
    tagline: "Smart Fitness Technology for Modern Gyms and Wellness Businesses",
    shortDescription:
      "Get Fit is a comprehensive fitness management platform designed to help gyms, fitness studios, and wellness providers deliver exceptional member experiences while streamlining daily operations.",
    fullDescription:
      "Get Fit is a comprehensive fitness management platform designed to help gyms, fitness studios, and wellness providers deliver exceptional member experiences while streamlining daily operations. The platform empowers members to stay engaged with their fitness goals while providing gym operators with the tools they need to manage memberships, trainers, classes, payments, and performance, all from a single ecosystem. Whether you are operating a single fitness centre or a multi-branch gym network, Get Fit helps you build stronger communities, improve retention, and drive sustainable growth.",
    category: "Fitness & Wellness",
    categoryId: "fitness-wellness",
    icon: "ri-heart-pulse-line",
    accentImage:
      "https://readdy.ai/api/search-image?query=3D%20isometric%20dark%20mode%20enterprise%20system%20visualization%20of%20a%20fitness%20and%20wellness%20management%20platform%20with%20member%20activity%20tracking%20dashboards%2C%20class%20scheduling%20grids%2C%20progress%20tracking%20charts%2C%20workout%20program%20cards%2C%20body%20measurement%20trend%20graphs%2C%20and%20gym%20equipment%20management%20nodes%20floating%20in%20a%20structured%20layered%20interface%2C%20clean%20minimal%20dark%20graphite%20background%20with%20subtle%20lighting%2C%20technical%20precise%20render%20style%2C%20no%20text%20no%20logos%2C%20no%20people%2C%20subtle%20emerald%20green%20accent%20highlights%20on%20achievement%20milestones%2C%20consistent%20lighting%20from%20upper%20left%2C%20sharp%20focus%2C%20high%20detail&width=900&height=500&seq=getfit-hero&orientation=landscape",
    features: [
      {
        title: "Digital Membership Management",
        description:
          "Centralized member profiles with subscription plans, attendance history, progress tracking, and communication preferences. Manage renewals, freezes, and upgrades from a single dashboard.",
        icon: "ri-id-card-line",
      },
      {
        title: "Class Booking & Scheduling",
        description:
          "Members browse and book classes through a mobile portal. Trainers manage capacity, waitlists, and cancellations. Calendar integrations prevent double-booking and keep schedules synchronized.",
        icon: "ri-calendar-event-line",
      },
      {
        title: "Workout Program Access",
        description:
          "Trainers create and assign structured workout programs with exercises, sets, reps, and video demonstrations. Members access their personalized programs anytime through the member portal.",
        icon: "ri-list-check",
      },
      {
        title: "Progress Tracking & Fitness Insights",
        description:
          "Track body measurements, weight, progress photos, step counts, water intake, and activity monitoring. Visual dashboards show trends and milestones to keep members motivated and engaged.",
        icon: "ri-line-chart-line",
      },
      {
        title: "Fitness Challenges & Rewards",
        description:
          "Create gym-wide or group challenges with leaderboards and achievement badges. Set goals, track participation, and reward top performers to build community and drive retention.",
        icon: "ri-trophy-line",
      },
      {
        title: "Trainer Management & Tools",
        description:
          "Manage trainer profiles, certifications, schedules, and client assignments. Trainers create programs, conduct assessments, monitor member progress, and communicate directly through the platform.",
        icon: "ri-user-star-line",
      },
      {
        title: "Subscription & Payment Management",
        description:
          "Handle membership billing, payment tracking, auto-renewals, and overdue alerts. Multiple plan types supported with flexible billing cycles and integrated payment reconciliation.",
        icon: "ri-bank-card-line",
      },
      {
        title: "Multi-Branch Administration",
        description:
          "Manage multiple gym locations from a centralized admin dashboard. Compare branch performance, member retention, class attendance, and revenue across your entire fitness network.",
        icon: "ri-building-line",
      },
      {
        title: "Revenue & Membership Analytics",
        description:
          "Real-time dashboards for membership growth, churn rate, class utilization, trainer performance, and revenue trends. Data-driven insights to optimize operations and marketing spend.",
        icon: "ri-bar-chart-box-line",
      },
      {
        title: "Promotional Campaign Management",
        description:
          "Create targeted promotions for membership drives, class packages, and seasonal offers. Track campaign performance, conversion rates, and ROI directly within the platform.",
        icon: "ri-megaphone-line",
      },
    ],
    useCases: [
      {
        title: "Commercial Gyms",
        description:
          "Manage thousands of members across multiple locations with structured membership tiers, automated billing, class scheduling, and trainer coordination from a centralized platform.",
      },
      {
        title: "Boutique Fitness Studios",
        description:
          "Deliver premium, personalized experiences with curated class schedules, trainer-led programs, and community-building challenges that keep boutique members engaged and loyal.",
      },
      {
        title: "Personal Training Centres",
        description:
          "Equip trainers with program creation tools, progress monitoring, and client communication features while giving members visibility into their fitness journey and achievements.",
      },
      {
        title: "Wellness & Lifestyle Clubs",
        description:
          "Offer holistic wellness experiences combining fitness classes, personal training, wellness challenges, and holistic progress tracking across multiple dimensions of health and wellbeing.",
      },
    ],
    screenshots: [
      {
        label: "Member Dashboard",
        image:
          "https://readdy.ai/api/search-image?query=3D%20isometric%20dark%20mode%20enterprise%20fitness%20member%20dashboard%20with%20activity%20rings%2C%20workout%20history%20timeline%2C%20upcoming%20class%20cards%2C%20progress%20charts%2C%20and%20achievement%20badges%2C%20clean%20minimal%20dark%20graphite%20UI%2C%20technical%20precise%20render%2C%20no%20text%2C%20subtle%20emerald%20green%20accents%2C%20consistent%20lighting%2C%20sharp%20focus%2C%20high%20detail&width=800&height=450&seq=getfit-ss1&orientation=landscape",
      },
      {
        label: "Class Schedule",
        image:
          "https://readdy.ai/api/search-image?query=3D%20isometric%20dark%20mode%20enterprise%20fitness%20class%20schedule%20with%20weekly%20calendar%20grid%2C%20class%20cards%20with%20trainer%20names%20and%20capacity%20indicators%2C%20booking%20buttons%2C%20and%20waitlist%20status%2C%20clean%20minimal%20dark%20graphite%20UI%2C%20technical%20precise%20render%2C%20no%20text%2C%20subtle%20emerald%20green%20accents%2C%20consistent%20lighting%2C%20sharp%20focus%2C%20high%20detail&width=800&height=450&seq=getfit-ss2&orientation=landscape",
      },
      {
        label: "Gym Analytics",
        image:
          "https://readdy.ai/api/search-image?query=3D%20isometric%20dark%20mode%20enterprise%20gym%20analytics%20dashboard%20with%20membership%20growth%20charts%2C%20retention%20rate%20gauges%2C%20class%20utilization%20heatmaps%2C%20revenue%20trend%20lines%2C%20and%20branch%20comparison%20panels%2C%20clean%20minimal%20dark%20graphite%20UI%2C%20technical%20precise%20render%2C%20no%20text%2C%20subtle%20emerald%20green%20accents%2C%20consistent%20lighting%2C%20sharp%20focus%2C%20high%20detail&width=800&height=450&seq=getfit-ss3&orientation=landscape",
      },
    ],
    integrations: ["HCM", "Accounting", "Admin", "Growth & Marketing"],
    benefits: [
      "Improve member engagement and retention with personalized fitness experiences",
      "Digitize gym operations and reduce administrative workload across all locations",
      "Deliver personalized fitness experiences with trainer-led programs and progress tracking",
      "Gain valuable insights through data-driven reporting on membership, revenue, and class performance",
    ],
    stats: [
      { value: "10", label: "Core capabilities" },
      { value: "Multi-branch", label: "Gym management" },
      { value: "100%", label: "Member visibility" },
    ],
  },
};

export const productIds = [
  "worktrack",
  "hris",
  "pms",
  "oms",
  "finance",
  "admin",
  "quality-audit",
  "learn",
  "remoteops-hub",
  "growth-marketing",
  "sales-crm",
  "workplace-services",
  "get-fit",
];

export function getProductById(id: string): ProductData | undefined {
  return productsData[id];
}